import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const TOKEN_KEY = "cshq_token";
const AuthContext = createContext(null);

const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(false); return; }
    setAuthHeader(t);
    axios.get(`${API}/auth/me`)
      .then((r) => setUser(r.data.user))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthHeader(null); setUser(false); });
  }, []);

  const persist = useCallback((tok, usr) => {
    localStorage.setItem(TOKEN_KEY, tok);
    setAuthHeader(tok);
    setToken(tok);
    setUser(usr);
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setToken(null);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
